import React from 'react';

import axios from 'axios';
import { useQuery } from 'react-query';

import { fetchPackingListsAdministration } from 'utils/ts-queries';
import { ApiPackingListAdministration } from 'types/apiReturns';

import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import Datetime from 'components/Shared/Datetime';

// Libellés des modes de retour (même ordre que ReportService.GetPackingListReportAsync).
const modeRetourLabels = ['Navette', "Ordre d'enlèvement", 'TNT'];

// Le numéro est une saisie libre : on tronque l'affichage et le contenu complet
// reste accessible au survol.
const longueurMaxNumero = 20;

function PackingListRow(props: {
    item: ApiPackingListAdministration,
    onErreur: (message: string) => void,
}) {
    const { item, onErreur } = props;

    const [enCours, setEnCours] = React.useState(false);

    const reediter = () => {

        setEnCours(true);

        axios.post(`api/packing-lists/${item.id}/exporter/`, {}, { responseType: 'blob' })
            .then((response) => {

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `packingList_${item.id}.pdf`);

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch((error) => {
                console.log(error);

                const statut = error.response && error.response.status;

                onErreur(statut === 404
                    ? `Packing list ${item.id} introuvable.`
                    : `Erreur à la génération du PDF de la packing list ${item.id}, veuillez contacter le support.`);
            })
            .then(() => setEnCours(false));
    };

    return (
        <TableRow hover>
            <TableCell sx={{ fontWeight: 'bold' }}>{item.id}</TableCell>
            <TableCell><Datetime withTime datetime={item.dateCreation} /></TableCell>
            <TableCell><Datetime datetime={item.dateExpedition} /></TableCell>
            <TableCell>{item.codeDepot}{item.nomDepot ? ` - ${item.nomDepot}` : ''}</TableCell>
            <TableCell>{item.libellePlateforme}</TableCell>
            <TableCell>{modeRetourLabels[item.modeRetour] || item.modeRetour}</TableCell>
            <TableCell>
                {!item.numero
                    ? '-'
                    : item.numero.length <= longueurMaxNumero
                        ? item.numero
                        : (
                            <Tooltip title={item.numero}>
                                <span style={{ cursor: 'help' }}>
                                    {`${item.numero.substring(0, longueurMaxNumero)}…`}
                                </span>
                            </Tooltip>
                        )}
            </TableCell>
            <TableCell align="right">{item.nbDossiers}</TableCell>
            <TableCell align="right">{item.nbPalettes}</TableCell>
            <TableCell align="right">{item.nbColis}</TableCell>
            <TableCell align="right">{item.nbLongueur}</TableCell>
            <TableCell>{item.emetteurNom || '-'}</TableCell>
            <TableCell align="center">
                {enCours
                    ? <CircularProgress size={20} />
                    : (
                        <Tooltip title="Rééditer le PDF">
                            <IconButton size="small" onClick={reediter}>
                                <PictureAsPdfIcon />
                            </IconButton>
                        </Tooltip>
                    )}
            </TableCell>
        </TableRow>
    );
}

export default function AdministrationPackingLists() {

    const [page, setPage] = React.useState(0);
    const [nbParPage, setNbParPage] = React.useState(25);
    const [filtreId, setFiltreId] = React.useState('');
    const [filtreCodeDepot, setFiltreCodeDepot] = React.useState('');
    const [triPar, setTriPar] = React.useState('id');
    const [triAscendant, setTriAscendant] = React.useState(false);
    const [notification, setNotification] = React.useState<{ open: boolean; message: string }>({ open: false, message: '' });

    const signalerErreur = React.useCallback(
        (message: string) => setNotification({ open: true, message }),
        []
    );

    const { data } = useQuery(
        ['packingListsAdministration', page, nbParPage, filtreId, filtreCodeDepot, triPar, triAscendant],
        () => fetchPackingListsAdministration({
            page,
            nbParPage,
            filtrePackingListId: filtreId ? parseInt(filtreId, 10) : null,
            filtreCodeDepot,
            triPar,
            triAscendant,
        }),
        { keepPreviousData: true }
    );

    // Clic sur une colonne triable : on inverse le sens si c'est déjà la colonne courante.
    const trierPar = (colonne: string) => {
        setPage(0);
        if (triPar === colonne) {
            setTriAscendant(!triAscendant);
        } else {
            setTriPar(colonne);
            setTriAscendant(false);
        }
    };

    return (
        <div>
            <Typography variant="h6" gutterBottom>Packing lists</Typography>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '90px' }} sortDirection={triPar === 'id' ? (triAscendant ? 'asc' : 'desc') : false}>
                                <TableSortLabel
                                    active={triPar === 'id'}
                                    direction={triPar === 'id' && triAscendant ? 'asc' : 'desc'}
                                    onClick={() => trierPar('id')}
                                >
                                    N°
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ width: '150px' }}>Créée le</TableCell>
                            <TableCell sx={{ width: '110px' }}>Expédition</TableCell>
                            <TableCell>Dépôt</TableCell>
                            <TableCell sx={{ width: '180px' }}>Plateforme</TableCell>
                            <TableCell sx={{ width: '150px' }}>Mode de retour</TableCell>
                            <TableCell sx={{ width: '140px' }}>Numéro</TableCell>
                            <TableCell sx={{ width: '90px' }} align="right" sortDirection={triPar === 'nbdossiers' ? (triAscendant ? 'asc' : 'desc') : false}>
                                <TableSortLabel
                                    active={triPar === 'nbdossiers'}
                                    direction={triPar === 'nbdossiers' && triAscendant ? 'asc' : 'desc'}
                                    onClick={() => trierPar('nbdossiers')}
                                >
                                    Dossiers
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ width: '90px' }} align="right">Palettes</TableCell>
                            <TableCell sx={{ width: '80px' }} align="right">Colis</TableCell>
                            <TableCell sx={{ width: '90px' }} align="right">Longueur</TableCell>
                            <TableCell sx={{ width: '160px' }}>Émetteur</TableCell>
                            <TableCell sx={{ width: '60px' }} align="center">PDF</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <TextField
                                    value={filtreId}
                                    onChange={(e) => { setPage(0); setFiltreId(e.target.value); }}
                                    size="small"
                                    type="number"
                                />
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell>
                                <TextField
                                    value={filtreCodeDepot}
                                    onChange={(e) => { setPage(0); setFiltreCodeDepot(e.target.value); }}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data && data.packingLists.map((item) => (
                            <PackingListRow key={item.id} item={item} onErreur={signalerErreur} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={data ? data.nbFiltres : 0}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={nbParPage}
                onRowsPerPageChange={(e) => { setNbParPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage="Par page"
            />

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setNotification({ ...notification, open: false })}
                    severity="error"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
